/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';
import customDataAction, {
    CustomGetFullProductsByCollectionInput,
    IFullProductsSearchResultsWithCount
} from './custom-get-full-products-by-collection.action';
import { BaseCollectionInput, createBaseCollectionInput } from './base-collection.action';

let productCollectionArgs: ICreateActionContext<{ itemsPerPage: number; includedAttributes: boolean | undefined }>;

export interface CustomerTypeProperties {
    Recommend: boolean;
    Endowment: boolean;
    Temple: boolean;
}
/**
 * GetFullProductsByCollection Action Input.
 */
export class GetFullProductsByCollectionInput extends BaseCollectionInput implements IActionInput {
    public getCacheKey = () => 'FullProductByCollection';
    /**
     * The cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'FullProductSearchResult';

    /**
     * The data cache type.
     * @returns The data cache type.
     */
    public dataCacheType = (): CacheType => {
        return 'none';
    };
}

/**
 * CreateInput function which creates and actionInput used to fetch products for a list page.
 * @param args - The API arguments.
 * @returns IActionInput - The action input.
 */
const createInput = (args: ICreateActionContext<{ itemsPerPage: number; includedAttributes: boolean | undefined }>): IActionInput => {
    productCollectionArgs = args;

    return createBaseCollectionInput(args, GetFullProductsByCollectionInput);
};

/**
 * Action function to fetch products for a list page.
 * @param input - The input.
 * @param context - The context.
 * @returns IFullProductsSearchResultsWithCount - The full product search result with count.
 */
async function action(input: GetFullProductsByCollectionInput, context: IActionContext): Promise<IFullProductsSearchResultsWithCount> {
    const customerType: CustomerTypeProperties = {
        Recommend: false,
        Endowment: false,
        Temple: false
    };
    let products: IFullProductsSearchResultsWithCount = {
        products: [],
        count: 0
    };
    const isUserAuthenticated = context.requestContext.user.isAuthenticated;
    if (isUserAuthenticated) {
        const customerInput = new GetCustomerInput(context.requestContext.apiSettings, context.requestContext.user.customerAccountNumber);
        const customer = await getCustomer(customerInput, context);

        customerType.Recommend = customer?.ExtensionProperties?.find(x => x.Key === 'CMISRecommend')?.Value?.BooleanValue || false;
        customerType.Endowment = customer?.ExtensionProperties?.find(x => x.Key === 'CMISEndowment')?.Value?.BooleanValue || false;
        customerType.Temple = customer?.ExtensionProperties?.find(x => x.Key === 'CMISTemple')?.Value?.BooleanValue || false;
    }

    const updatedArgs: ICreateActionContext = {
        requestContext: context.requestContext,
        config: productCollectionArgs.config,
        data: productCollectionArgs.data
    };

    const baseCollectionInput: BaseCollectionInput = createBaseCollectionInput(
        updatedArgs,
        CustomGetFullProductsByCollectionInput,
        customerType
    );

    //LDS DEV: Removing Endowment, Temple and Recommend refiners to default to no when no user is logged in.
    //Found a way to show unauthorized products to users who are not signed in with refiners so removing this for now.

    let updatedRefiners: any = [];

    if (input.refiners.length > 0) {
        if (isUserAuthenticated === false) {
            updatedRefiners = input.refiners.filter(refiner => {
                return (
                    refiner.LeftValueBoundString !== 'Endowment_YES' &&
                    refiner.RightValueBoundString !== 'Endowment_YES' &&
                    refiner.LeftValueBoundString !== 'Temple_YES' &&
                    refiner.RightValueBoundString !== 'Temple_YES' &&
                    refiner.LeftValueBoundString !== 'Recommend_YES' &&
                    refiner.RightValueBoundString !== 'Recommend_YES'
                );
            });
        }
        baseCollectionInput.refiners = updatedRefiners && updatedRefiners.length > 0 ? updatedRefiners : input.refiners;
    } else {
        baseCollectionInput.refiners = [];
    }
    // Set Top
    if (baseCollectionInput.queryResultSettings.Paging && productCollectionArgs.config) {
        baseCollectionInput.queryResultSettings.Paging.Top = productCollectionArgs.config.itemsPerPage || 1;
    }

    // Set Skip
    if (
        baseCollectionInput.queryResultSettings.Paging &&
        productCollectionArgs.requestContext.query &&
        productCollectionArgs.requestContext.query.skip
    ) {
        baseCollectionInput.queryResultSettings.Paging.Skip = +productCollectionArgs.requestContext.query.skip;
    }

    if (input.queryResultSettings.Sorting && input.queryResultSettings.Sorting.Columns) {
        baseCollectionInput.queryResultSettings.Sorting = input.queryResultSettings.Sorting;
    }

    baseCollectionInput.queryResultSettings.count = true;

    products = await customDataAction(baseCollectionInput, context);

    return products;
}

export const actionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/search-result-container/get-full-products-by-collection',
    action: <IAction<IFullProductsSearchResultsWithCount>>action,
    input: createInput
});

export default actionDataAction;
